import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiArrowLeft, FiMinus, FiPlus, FiX, FiLoader, FiSearch, FiCheck, FiMessageSquare, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { networkManager } from '../../systems/NetworkManager';
import { PriceHistoryChart } from './PriceHistoryChart';
import {
  MarketItemSummary,
  OrderBook,
  MarketOrderData,
  InventoryItem,
  MarketTransaction,
  ItemType,
  MARKETPLACE_CONFIG,
  ALL_EQUIPMENT,
  SHIPS,
  RESOURCES,
  ICE_RESOURCES,
  GAS_RESOURCES,
  REFINED_RESOURCES,
  Equipment,
} from '@astra/shared';

import {
  PALETTE, SLOT_COLORS,
  stationContainerStyle, stationHeaderStyle, stationBackButtonStyle,
  stationHeaderTabsStyle, stationHeaderTabStyle as stationHeaderTabStyleFn, stationHintStyle,
} from './stationTheme';

interface GameItem {
  itemType: ItemType;
  itemId: string;
  itemName: string;
  category?: string;
  subCategory?: string;
}

// Helper to extract sub-category from equipment
function getEquipmentSubCategory(eq: typeof ALL_EQUIPMENT[keyof typeof ALL_EQUIPMENT]): string | undefined {
  if ('weaponType' in eq) return (eq as any).weaponType;
  if ('shieldType' in eq) return (eq as any).shieldType;
  if ('engineType' in eq) return (eq as any).engineType;
  if ('toolType' in eq) return (eq as any).toolType;
  if ('enhancerType' in eq) return (eq as any).enhancerType;
  if ('utilityType' in eq) return (eq as any).utilityType;
  return undefined;
}

// Build a static list of ALL items in the game
const ALL_GAME_ITEMS: GameItem[] = [
  // Equipment
  ...Object.values(ALL_EQUIPMENT).map(eq => ({
    itemType: 'equipment' as ItemType,
    itemId: eq.id,
    itemName: eq.name,
    category: eq.category,
    subCategory: getEquipmentSubCategory(eq),
  })),
  // Ships
  ...Object.values(SHIPS).map(ship => ({
    itemType: 'ship' as ItemType,
    itemId: ship.id,
    itemName: ship.name,
    category: ship.class,
  })),
  // Ore resources (legacy)
  ...Object.entries(RESOURCES).map(([key, res]) => ({
    itemType: 'resource' as ItemType,
    itemId: key,
    itemName: res.name,
    category: 'ore',
  })),
  // Ice resources
  ...Object.entries(ICE_RESOURCES).map(([key, res]) => ({
    itemType: 'resource' as ItemType,
    itemId: key,
    itemName: res.name,
    category: 'ice',
  })),
  // Gas resources
  ...Object.entries(GAS_RESOURCES).map(([key, res]) => ({
    itemType: 'resource' as ItemType,
    itemId: key,
    itemName: res.name,
    category: 'gas',
  })),
  // Refined resources
  ...Object.entries(REFINED_RESOURCES).map(([key, res]) => ({
    itemType: 'resource' as ItemType,
    itemId: key,
    itemName: res.name,
    category: 'refined',
  })),
];

const CATEGORY_FILTERS = [
  { value: 'ships', label: 'Ships' },
  { value: 'resources', label: 'Resources' },
  { value: 'weapons', label: 'Weapons' },
  { value: 'shields', label: 'Shields' },
  { value: 'engines', label: 'Engines' },
  { value: 'tools', label: 'Tools' },
  { value: 'enhancers', label: 'Enhancers' },
  { value: 'utilities', label: 'Utilities' },
] as const;

const SUB_CATEGORY_FILTERS: Record<string, { value: string; label: string }[]> = {
  ships: [
    { value: 'corvette', label: 'Corvette' },
    { value: 'frigate', label: 'Frigate' },
    { value: 'destroyer', label: 'Destroyer' },
    { value: 'cruiser', label: 'Cruiser' },
    { value: 'battleship', label: 'Battleship' },
    { value: 'carrier', label: 'Carrier' },
    { value: 'dreadnought', label: 'Dreadnought' },
    { value: 'hauler', label: 'Hauler' },
  ],
  resources: [
    { value: 'ore', label: 'Ore' },
    { value: 'ice', label: 'Ice' },
    { value: 'gas', label: 'Gas' },
    { value: 'refined', label: 'Refined' },
  ],
  weapons: [
    { value: 'kinetic', label: 'Kinetic' },
    { value: 'laser', label: 'Laser' },
    { value: 'missile', label: 'Missile' },
  ],
  shields: [
    { value: 'deflector', label: 'Deflector' },
    { value: 'absorber', label: 'Absorber' },
    { value: 'regenerator', label: 'Regenerator' },
  ],
  engines: [
    { value: 'proton', label: 'Proton' },
    { value: 'ion', label: 'Ion' },
    { value: 'plasma', label: 'Plasma' },
  ],
};

// Helper to look up full item details for the info panel
function getItemDetails(itemType: ItemType, itemId: string): {
  description: string;
  tier?: number;
  value?: number;
  stats: { label: string; value: string }[];
  tags: string[];
} | null {
  if (itemType === 'ship') {
    const ship = Object.values(SHIPS).find(s => s.id === itemId);
    if (!ship) return null;
    return {
      description: ship.description,
      tier: ship.tier,
      value: undefined,
      tags: [ship.class, ship.category, ship.size],
      stats: [
        { label: 'Hull', value: String(ship.stats.hull) },
        { label: 'Shield', value: String(ship.stats.shield) },
        { label: 'Speed', value: String(ship.stats.baseMaxSpeed) },
        { label: 'Accel', value: String(ship.stats.baseAcceleration) },
        { label: 'Cargo', value: String(ship.stats.cargoCapacity) },
        { label: 'Mass', value: String(ship.stats.mass) },
        { label: 'Slots', value: String(ship.slots.length) },
        { label: 'Sensor', value: String(ship.stats.sensorRange) },
      ],
    };
  }

  if (itemType === 'equipment') {
    const eq = ALL_EQUIPMENT[itemId as keyof typeof ALL_EQUIPMENT] as Equipment | undefined;
    if (!eq) return null;
    const tags = [eq.category.toLowerCase(), `${eq.size.toLowerCase()} slot`];
    let stats: { label: string; value: string }[] = [
      { label: 'Value', value: `${eq.value.toLocaleString()} CR` },
      { label: 'Volume', value: String(eq.volume) },
    ];

    if ('weaponType' in eq) {
      const w = eq as any;
      tags.unshift(w.weaponType);
      stats = [
        { label: 'Damage', value: String(w.stats.damage) },
        { label: 'Fire Rate', value: `${w.stats.fireRate}/s` },
        { label: 'Range', value: String(w.stats.range) },
        { label: 'Proj Speed', value: String(w.stats.projectileSpeed) },
        { label: 'Heat/Shot', value: String(w.stats.heatPerShot) },
        { label: 'Value', value: `${eq.value.toLocaleString()} CR` },
      ];
    } else if ('shieldType' in eq) {
      const s = eq as any;
      tags.unshift(s.shieldType);
      stats = [
        { label: 'Capacity', value: String(s.stats.shieldCapacity) },
        { label: 'Recharge', value: `${s.stats.rechargeRate}/s` },
        { label: 'Delay', value: `${s.stats.rechargeDelay}s` },
        { label: 'Value', value: `${eq.value.toLocaleString()} CR` },
      ];
    } else if ('engineType' in eq) {
      const e = eq as any;
      tags.unshift(e.engineType);
      stats = [
        { label: 'Accel', value: `+${e.stats.accelerationBonus}` },
        { label: 'Speed', value: `+${e.stats.maxSpeedBonus}` },
        { label: 'Strafe', value: `+${e.stats.strafeBonus}` },
        { label: 'Accel ×', value: `${e.stats.accelerationMultiplier}` },
        { label: 'Speed ×', value: `${e.stats.maxSpeedMultiplier}` },
        { label: 'Value', value: `${eq.value.toLocaleString()} CR` },
      ];
    } else if ('toolType' in eq) {
      const t = eq as any;
      tags.unshift(t.toolType.replace('_', ' '));
      stats = [
        { label: 'Mining Pwr', value: String(t.stats.miningPower) },
        { label: 'Range', value: String(t.stats.range) },
        { label: 'Efficiency', value: `${t.stats.efficiency}×` },
        { label: 'Value', value: `${eq.value.toLocaleString()} CR` },
      ];
    } else if (eq.category === 'ENHANCER') {
      const u = eq as any;
      const eStats: { label: string; value: string }[] = [];
      if (u.stats?.hullBonus) eStats.push({ label: 'Hull', value: `+${u.stats.hullBonus}` });
      if (u.stats?.cargoBonus) eStats.push({ label: 'Cargo', value: `+${u.stats.cargoBonus}` });
      if (u.stats?.sensorBonus) eStats.push({ label: 'Sensor', value: `+${u.stats.sensorBonus}` });
      if (u.stats?.boostSpeedMultiplier) eStats.push({ label: 'Boost', value: `${u.stats.boostSpeedMultiplier}×` });
      if (u.stats?.warpDisruptRange) eStats.push({ label: 'Range', value: `${u.stats.warpDisruptRange}u` });
      if (u.stats?.droneCapacity) eStats.push({ label: 'Drones', value: `${u.stats.droneCapacity}` });
      eStats.push({ label: 'Value', value: `${eq.value.toLocaleString()} CR` });
      stats = eStats;
    } else if (eq.category === 'UTILITY') {
      const u = eq as any;
      const uStats: { label: string; value: string }[] = [];
      if (u.stats?.scanRange) uStats.push({ label: 'Range', value: `${u.stats.scanRange}u` });
      if (u.stats?.scanDuration) uStats.push({ label: 'Scan', value: `${(u.stats.scanDuration / 1000).toFixed(1)}s` });
      if (u.stats?.beaconTier) uStats.push({ label: 'Beacon Tier', value: `T${u.stats.beaconTier}` });
      if (u.stats?.beaconDeployCooldown) uStats.push({ label: 'Cooldown', value: `${(u.stats.beaconDeployCooldown / 1000)}s` });
      uStats.push({ label: 'Value', value: `${eq.value.toLocaleString()} CR` });
      stats = uStats;
    }

    return {
      description: eq.description,
      tier: eq.tier,
      value: eq.value,
      tags,
      stats,
    };
  }

  if (itemType === 'resource') {
    // Check all resource maps
    const oreTiers: Record<string, number> = { iron: 1, copper: 2, titanium: 3, platinum: 4 };
    const allRes: Record<string, { tier: number; value: number; name: string; source: string }> = {};
    for (const [k, v] of Object.entries(RESOURCES)) {
      allRes[k] = { ...(v as any), tier: oreTiers[k] || 1, source: 'Asteroids' };
    }
    for (const [k, v] of Object.entries(ICE_RESOURCES)) {
      allRes[k] = { ...(v as any), source: 'Ice Asteroids' };
    }
    for (const [k, v] of Object.entries(GAS_RESOURCES)) {
      allRes[k] = { ...(v as any), source: 'Nebulae' };
    }
    for (const [k, v] of Object.entries(REFINED_RESOURCES)) {
      allRes[k] = { ...(v as any), source: 'Refineries' };
    }
    const res = allRes[itemId];
    if (!res) return null;
    return {
      description: `${res.source}-sourced resource.`,
      tier: res.tier,
      value: res.value,
      tags: [res.source.toLowerCase()],
      stats: [
        { label: 'Base Value', value: `${res.value} CR` },
        { label: 'Source', value: res.source },
        { label: 'Tier', value: `T${res.tier}` },
      ],
    };
  }

  return null;
}

interface MarketShopProps {
  stationId: string;
  onBack: () => void;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function getItemDisplayName(itemId: string): string {
  return itemId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Get daily limit based on activity level
function getMmDailyLimit(activityLevel?: 'low' | 'medium' | 'high'): number {
  switch (activityLevel) {
    case 'high': return 100;
    case 'medium': return 200;
    case 'low':
    default: return 500;
  }
}

type ChatChannel = 'global' | 'local' | 'party' | 'corp';

interface ChatMessage {
  id: string;
  playerId: string;
  playerName: string;
  channel: ChatChannel;
  message: string;
  timestamp: number;
  isSystem?: boolean;
}

export function MarketShop({ stationId, onBack }: MarketShopProps) {
  // Filter state - checkboxes, both can be on or off
  const [filterAvailable, setFilterAvailable] = useState(false);
  const [filterOwned, setFilterOwned] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState<string>('all');

  // Chat state
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatChannel, setChatChannel] = useState<ChatChannel>('local');
  const [chatInput, setChatInput] = useState('');
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);

  // Resizable sections state (percentages)
  const [sellersHeight, setSellersHeight] = useState(30);
  const [buyersHeight, setBuyersHeight] = useState(30);
  const [tradesHeight, setTradesHeight] = useState(40);
  const [activeDivider, setActiveDivider] = useState<'sellers-buyers' | 'buyers-trades' | null>(null);
  const centerPanelRef = useRef<HTMLDivElement>(null);

  // Horizontal resizable state (left panel width)
  const [leftPanelWidth, setLeftPanelWidth] = useState(240);
  const [isResizingHorizontal, setIsResizingHorizontal] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Data from server
  const [marketItems, setMarketItems] = useState<MarketItemSummary[]>([]);
  const [selectedItem, setSelectedItem] = useState<{ itemType: ItemType; itemId: string; itemName: string } | null>(null);
  const selectedItemRef = useRef(selectedItem); // Ref to avoid stale closure in callbacks
  const [orderBook, setOrderBook] = useState<OrderBook | null>(null);
  const [recentTrades, setRecentTrades] = useState<MarketTransaction[]>([]);
  const [playerInventory, setPlayerInventory] = useState<InventoryItem[]>([]);
  const [launderingRate, setLaunderingRate] = useState<number>(0);
  const [isSettlementMarket, setIsSettlementMarket] = useState<boolean>(false);
  const [playerOrders, setPlayerOrders] = useState<MarketOrderData[]>([]);
  // Market Maker prices and availability
  const [mmPrices, setMmPrices] = useState<{
    stationId: string;
    itemId: string;
    buyPrice?: number;
    sellPrice?: number;
    spread?: number;
    activityLevel?: 'low' | 'medium' | 'high';
    buyLimitRemaining?: number;
    sellLimitRemaining?: number;
    supported: boolean;
  } | null>(null);

  // Order entry form
  const [orderMode, setOrderMode] = useState<'limit' | 'market'>('limit');
  const [orderType, setOrderType] = useState<'buy' | 'sell'>('buy');
  const [orderPrice, setOrderPrice] = useState<string>('');
  const [orderQuantity, setOrderQuantity] = useState<string>('1');
  const [buyDestination, setBuyDestination] = useState<'cargo' | 'bank'>('cargo');
  const [sellSource, setSellSource] = useState<'cargo' | 'bank'>('cargo');
  const [cargoUsed, setCargoUsed] = useState<number>(0);
  const [cargoCapacity, setCargoCapacity] = useState<number>(0);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'market' | 'my-orders'>('market');
  const [centerTab, setCenterTab] = useState<'market-data' | 'price-history'>('market-data');
  const [priceHistory, setPriceHistory] = useState<{ time: number; price: number; volume: number }[]>([]);

  // Keep selectedItemRef in sync with selectedItem state
  useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  // Auto-switch buy destination to bank when cargo is full
  useEffect(() => {
    if (cargoUsed >= cargoCapacity && cargoCapacity > 0 && buyDestination === 'cargo') {
      setBuyDestination('bank');
    }
  }, [cargoUsed, cargoCapacity, buyDestination]);

  // Handle ESC key to go back
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onBack]);

  // Setup network callbacks
  useEffect(() => {
    networkManager.onStationMarketsData = (data) => {
      setMarketItems(data.markets);
      setIsLoading(false);
    };

    networkManager.onOrderBookData = (data) => {
      setOrderBook(data.orderBook);
    };

    networkManager.onPlayerInventoryData = (data) => {
      setPlayerInventory(data.items);
      if (data.cargoUsed !== undefined) setCargoUsed(data.cargoUsed);
      if (data.cargoCapacity !== undefined) setCargoCapacity(data.cargoCapacity);
      if (data.launderingRate !== undefined) setLaunderingRate(data.launderingRate);
      if (data.isSettlement !== undefined) setIsSettlementMarket(data.isSettlement);
    };

    networkManager.onMyOrdersData = (data) => {
      setPlayerOrders(data.orders);
    };

    networkManager.onMarketHistoryData = (data) => {
      setRecentTrades(data.history);
      // Convert to price history format for chart
      const chartData = data.history.map((trade: MarketTransaction) => ({
        time: trade.executedAt,
        price: trade.pricePerUnit,
        volume: trade.quantity,
      }));
      // Sort by time ascending for chart
      chartData.sort((a, b) => a.time - b.time);
      setPriceHistory(chartData);
    };

    networkManager.onMarketMakerPricesData = (data) => {
      setMmPrices(data);
    };

    networkManager.onOrderResult = (data) => {
      setIsPlacingOrder(false);
      if (data.success) {
        toast.success('Order placed successfully!');
        setOrderPrice('');
        setOrderQuantity('1');
        networkManager.requestStationMarkets(stationId);
        networkManager.requestMyOrders(stationId);
        networkManager.requestPlayerInventory(stationId);
        // Use ref to get current selectedItem (avoid stale closure)
        const currentItem = selectedItemRef.current;
        if (currentItem) {
          networkManager.requestOrderBook(stationId, currentItem.itemType, currentItem.itemId);
          networkManager.requestMarketHistory(stationId, currentItem.itemType, currentItem.itemId, 100);
        }
      } else {
        toast.error(data.error || 'Failed to place order');
      }
    };

    networkManager.onMarketOrderResult = (data) => {
      setIsPlacingOrder(false);
      if (data.success) {
        const totalFilled = data.fills.reduce((sum, f) => sum + f.quantity, 0);
        // Check if Market Maker was used (any fill with orderId 'market_maker')
        const mmFill = data.fills.find(f => f.orderId === 'market_maker');
        if (mmFill) {
          toast.success(`Order filled via Market Maker! ${mmFill.quantity} units at ${formatNumber(mmFill.pricePerUnit)} CR each.`);
        } else {
          toast.success(`Market order executed! Filled ${totalFilled} units.`);
        }
        // Notify if items were sent to bank due to full cargo
        if (data.sentToBank) {
          toast.info('Cargo full — items sent to station bank.');
        }
        setOrderPrice('');
        setOrderQuantity('1');
        networkManager.requestStationMarkets(stationId);
        networkManager.requestMyOrders(stationId);
        networkManager.requestPlayerInventory(stationId);
        // Use ref to get current selectedItem (avoid stale closure)
        const currentItem = selectedItemRef.current;
        if (currentItem) {
          networkManager.requestOrderBook(stationId, currentItem.itemType, currentItem.itemId);
          networkManager.requestMarketHistory(stationId, currentItem.itemType, currentItem.itemId, 100);
          // Refresh MM prices after trade
          if (currentItem.itemType === 'resource') {
            networkManager.requestMarketMakerPrices(stationId, currentItem.itemId);
          }
        }
      } else {
        toast.error(data.error || 'Market order failed');
      }
    };

    networkManager.onCancelResult = (data) => {
      setCancellingOrderId(null);
      if (data.success) {
        toast.success('Order cancelled');
        networkManager.requestMyOrders(stationId);
        networkManager.requestStationMarkets(stationId);
      } else {
        toast.error(data.error || 'Failed to cancel order');
      }
    };

    // Chat message callback
    networkManager.onChatMessage = (data) => {
      setChatMessages(prev => {
        const newMessages = [...prev, data];
        // Keep max 100 messages
        while (newMessages.length > 100) {
          newMessages.shift();
        }
        return newMessages;
      });
    };

    // Initial fetch
    setIsLoading(true);
    networkManager.requestStationMarkets(stationId);
    networkManager.requestPlayerInventory(stationId);
    networkManager.requestMyOrders(stationId);

    return () => {
      networkManager.onStationMarketsData = null;
      networkManager.onOrderBookData = null;
      networkManager.onPlayerInventoryData = null;
      networkManager.onMyOrdersData = null;
      networkManager.onMarketHistoryData = null;
      networkManager.onMarketMakerPricesData = null;
      networkManager.onOrderResult = null;
      networkManager.onMarketOrderResult = null;
      networkManager.onCancelResult = null;
      networkManager.onChatMessage = null;
      networkManager.onOrderBookUpdated = null;
    };
  }, [stationId]);

  // Real-time order book updates - separate effect to avoid stale closure on selectedItem
  useEffect(() => {
    networkManager.onOrderBookUpdated = (data) => {
      // If we're viewing this item, refresh the order book
      if (selectedItem &&
          data.stationId === stationId &&
          data.itemType === selectedItem.itemType &&
          data.itemId === selectedItem.itemId) {
        networkManager.requestOrderBook(stationId, selectedItem.itemType, selectedItem.itemId);
        networkManager.requestMarketHistory(stationId, selectedItem.itemType, selectedItem.itemId, 100);
      }
      // Also refresh station markets to update best bid/ask display
      networkManager.requestStationMarkets(stationId);
      // Refresh player orders in case ours was matched
      networkManager.requestMyOrders(stationId);
    };

    return () => {
      networkManager.onOrderBookUpdated = null;
    };
  }, [stationId, selectedItem]);

  // Build unified item list from ALL game items + market data + inventory
  const allItems = useMemo(() => {
    const itemMap = new Map<string, { itemType: ItemType; itemId: string; itemName: string; category?: string; subCategory?: string; marketData?: MarketItemSummary; owned: number; ownedCargo: number; ownedBank: number; dirtyCargo?: number; dirtyBank?: number; hasMarketOrders: boolean }>();

    // Start with ALL game items
    for (const gameItem of ALL_GAME_ITEMS) {
      itemMap.set(gameItem.itemId, {
        itemType: gameItem.itemType,
        itemId: gameItem.itemId,
        itemName: gameItem.itemName,
        category: gameItem.category,
        subCategory: gameItem.subCategory,
        owned: 0,
        ownedCargo: 0,
        ownedBank: 0,
        hasMarketOrders: false,
      });
    }

    // Add market data
    for (const item of marketItems) {
      const existing = itemMap.get(item.itemId);
      const hasOrders = item.bestBid !== null || item.bestAsk !== null;
      if (existing) {
        existing.marketData = item;
        existing.hasMarketOrders = hasOrders;
      } else {
        // Item not in game definitions but has market orders
        itemMap.set(item.itemId, {
          itemType: item.itemType,
          itemId: item.itemId,
          itemName: item.itemName,
          marketData: item,
          owned: 0,
          ownedCargo: 0,
          ownedBank: 0,
          hasMarketOrders: hasOrders,
        });
      }
    }

    // Add inventory data (now includes both cargo and bank with source)
    for (const inv of playerInventory) {
      const existing = itemMap.get(inv.itemId);
      const invDirty = inv.dirty ?? 0;
      if (existing) {
        existing.owned += inv.quantity;
        if (inv.source === 'bank') {
          existing.ownedBank += inv.quantity;
          existing.dirtyBank = (existing.dirtyBank ?? 0) + invDirty;
        } else {
          existing.ownedCargo += inv.quantity;
          existing.dirtyCargo = (existing.dirtyCargo ?? 0) + invDirty;
        }
      } else {
        itemMap.set(inv.itemId, {
          itemType: inv.itemType as ItemType,
          itemId: inv.itemId,
          itemName: getItemDisplayName(inv.itemId),
          owned: inv.quantity,
          ownedCargo: inv.source === 'bank' ? 0 : inv.quantity,
          ownedBank: inv.source === 'bank' ? inv.quantity : 0,
          dirtyCargo: inv.source === 'bank' ? 0 : invDirty,
          dirtyBank: inv.source === 'bank' ? invDirty : 0,
          hasMarketOrders: false,
        });
      }
    }

    // Sort by name
    return Array.from(itemMap.values()).sort((a, b) => a.itemName.localeCompare(b.itemName));
  }, [marketItems, playerInventory]);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = allItems;

    // Apply category filter
    if (categoryFilter !== 'all') {
      items = items.filter(item => {
        switch (categoryFilter) {
          case 'ships': return item.itemType === 'ship';
          case 'resources': return item.itemType === 'resource';
          case 'weapons': return item.itemType === 'equipment' && item.category === 'WEAPON';
          case 'shields': return item.itemType === 'equipment' && item.category === 'SHIELD';
          case 'engines': return item.itemType === 'equipment' && item.category === 'ENGINE';
          case 'tools': return item.itemType === 'equipment' && item.category === 'TOOL';
          case 'enhancers': return item.itemType === 'equipment' && item.category === 'ENHANCER';
          case 'utilities': return item.itemType === 'equipment' && item.category === 'UTILITY';
          default: return true;
        }
      });
    }

    // Apply sub-category filter
    if (subCategoryFilter !== 'all') {
      items = items.filter(item => {
        // For ships, sub-category matches against the ship class (stored in category)
        if (item.itemType === 'ship') return item.category === subCategoryFilter;
        // For resources, sub-category matches against the resource type (stored in category)
        if (item.itemType === 'resource') return item.category === subCategoryFilter;
        // For equipment, sub-category matches against weaponType/shieldType/engineType
        return item.subCategory === subCategoryFilter;
      });
    }

    // Apply checkbox filters (if neither is checked, show all items)
    if (filterAvailable || filterOwned) {
      items = items.filter(item => {
        const matchesAvailable = filterAvailable && item.hasMarketOrders;
        const matchesOwned = filterOwned && item.owned > 0;
        return matchesAvailable || matchesOwned;
      });
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => item.itemName.toLowerCase().includes(query));
    }

    return items;
  }, [allItems, filterAvailable, filterOwned, searchQuery, categoryFilter, subCategoryFilter]);

  // Get current item's owned quantities (total, cargo, bank)
  const currentItemData = selectedItem ? allItems.find(i => i.itemId === selectedItem.itemId) : null;
  const currentItemOwned = currentItemData?.owned || 0;
  const currentItemOwnedCargo = currentItemData?.ownedCargo || 0;
  const currentItemOwnedBank = currentItemData?.ownedBank || 0;
  // Stolen (dirty) units of the selected item in the chosen sell source — drives the laundering preview.
  const currentItemDirtyInSource = sellSource === 'bank' ? (currentItemData?.dirtyBank ?? 0) : (currentItemData?.dirtyCargo ?? 0);
  // For selling, limit to quantity from selected source
  const currentSellableQuantity = sellSource === 'bank' ? currentItemOwnedBank : currentItemOwnedCargo;

  // Handle item selection
  const handleSelectItem = useCallback((item: typeof allItems[0]) => {
    setSelectedItem({ itemType: item.itemType, itemId: item.itemId, itemName: item.itemName });
    networkManager.requestOrderBook(stationId, item.itemType, item.itemId);
    networkManager.requestMarketHistory(stationId, item.itemType, item.itemId, 100);

    // Request Market Maker prices for resources
    if (item.itemType === 'resource') {
      networkManager.requestMarketMakerPrices(stationId, item.itemId);
    } else {
      setMmPrices(null); // Clear MM prices for non-resources
    }

    // Set default price based on order type
    if (item.marketData) {
      if (orderType === 'buy' && item.marketData.bestAsk) {
        setOrderPrice(item.marketData.bestAsk.toString());
      } else if (orderType === 'sell' && item.marketData.bestBid) {
        setOrderPrice(item.marketData.bestBid.toString());
      }
    }
  }, [stationId, orderType]);

  // Place order
  const handlePlaceOrder = useCallback(() => {
    if (!selectedItem || isPlacingOrder) return;

    const qty = parseInt(orderQuantity, 10);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Invalid quantity');
      return;
    }

    // Validate sell quantity doesn't exceed inventory from selected source
    if (orderType === 'sell' && qty > currentSellableQuantity) {
      toast.error(`You only have ${currentSellableQuantity} in ${sellSource} to sell`);
      return;
    }

    setIsPlacingOrder(true);

    const dest = orderType === 'buy' ? buyDestination : undefined;
    const src = orderType === 'sell' ? sellSource : undefined;

    if (orderMode === 'market') {
      networkManager.sendCreateMarketOrder(stationId, selectedItem.itemType, selectedItem.itemId, orderType, qty, dest, src);
    } else {
      const price = parseInt(orderPrice, 10);
      if (isNaN(price) || price <= 0) {
        toast.error('Invalid price');
        setIsPlacingOrder(false);
        return;
      }
      networkManager.sendCreateLimitOrder(stationId, selectedItem.itemType, selectedItem.itemId, orderType, price, qty, dest, src);
    }
  }, [selectedItem, orderQuantity, orderPrice, orderMode, orderType, stationId, isPlacingOrder, currentSellableQuantity, buyDestination, sellSource]);

  // Cancel order
  const handleCancelOrder = useCallback((orderId: string) => {
    if (cancellingOrderId) return;
    setCancellingOrderId(orderId);
    networkManager.sendCancelOrder(orderId);
  }, [cancellingOrderId]);

  // Chat functions
  const handleSendChat = useCallback(() => {
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    networkManager.sendChatMessage(chatChannel, trimmed);
    setChatInput('');
  }, [chatInput, chatChannel]);

  const handleChatKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendChat();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const channels: ChatChannel[] = ['global', 'local', 'party'];
      const idx = channels.indexOf(chatChannel);
      setChatChannel(channels[(idx + 1) % channels.length]);
    }
    e.stopPropagation();
  }, [handleSendChat, chatChannel]);

  // Auto-scroll chat
  useEffect(() => {
    if (isChatExpanded) {
      chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isChatExpanded]);

  const filteredChatMessages = chatMessages.filter(m => m.channel === chatChannel);

  const formatChatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatTradeTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Format expiry time like EVE Online: "89d 16h 15m"
  const formatExpiresIn = (expiryTimestamp: number) => {
    const now = Date.now();
    const diffMs = expiryTimestamp - now;

    if (diffMs <= 0) return 'Expired';

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (days === 0 && hours === 0 && seconds > 0) parts.push(`${seconds}s`);

    return parts.join(' ') || '< 1m';
  };

  // Divider drag handlers
  const handleDividerMouseDown = useCallback((e: React.MouseEvent, divider: 'sellers-buyers' | 'buyers-trades') => {
    e.preventDefault();
    setActiveDivider(divider);
  }, []);

  useEffect(() => {
    if (!activeDivider) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!centerPanelRef.current) return;
      const rect = centerPanelRef.current.getBoundingClientRect();
      const totalHeight = rect.height - 100; // Account for header and spread
      const mouseY = e.clientY - rect.top - 70; // Offset for header
      const percentage = (mouseY / totalHeight) * 100;

      if (activeDivider === 'sellers-buyers') {
        const newSellersHeight = Math.max(15, Math.min(60, percentage));
        const remaining = 100 - newSellersHeight;
        const buyersRatio = buyersHeight / (buyersHeight + tradesHeight);
        setSellersHeight(newSellersHeight);
        setBuyersHeight(remaining * buyersRatio);
        setTradesHeight(remaining * (1 - buyersRatio));
      } else if (activeDivider === 'buyers-trades') {
        const combinedBuyersTrades = buyersHeight + tradesHeight;
        const newBuyersEnd = Math.max(sellersHeight + 15, Math.min(85, percentage));
        const newBuyersHeight = newBuyersEnd - sellersHeight;
        const newTradesHeight = 100 - sellersHeight - newBuyersHeight;
        if (newBuyersHeight >= 15 && newTradesHeight >= 15) {
          setBuyersHeight(newBuyersHeight);
          setTradesHeight(newTradesHeight);
        }
      }
    };

    const handleMouseUp = () => {
      setActiveDivider(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeDivider, sellersHeight, buyersHeight, tradesHeight]);

  // Horizontal divider drag handler
  const handleHorizontalDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingHorizontal(true);
  }, []);

  useEffect(() => {
    if (!isResizingHorizontal) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!mainContentRef.current) return;
      const rect = mainContentRef.current.getBoundingClientRect();
      const newWidth = e.clientX - rect.left;
      setLeftPanelWidth(Math.max(180, Math.min(400, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizingHorizontal(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingHorizontal]);

  // Calculate order totals
  const price = parseInt(orderPrice, 10) || 0;
  const quantity = parseInt(orderQuantity, 10) || 0;
  const subtotal = price * quantity;
  const fee = Math.ceil(subtotal * MARKETPLACE_CONFIG.TRANSACTION_FEE_RATE);
  // Laundering on a SELL of stolen units (dirty-first). Charged at NPC venues; WAIVED at settlements
  // (we still compute the would-be amount to show the player what they save by selling there).
  const launderUnits = orderType === 'sell' ? Math.min(quantity, currentItemDirtyInSource) : 0;
  const wouldBeLaundering = Math.floor(launderUnits * price * launderingRate);
  const launderingFee = isSettlementMarket ? 0 : wouldBeLaundering;
  const total = orderType === 'buy' ? subtotal + fee : subtotal - fee - launderingFee;

  const asks = orderBook?.asks || [];
  const bids = orderBook?.bids || [];

  // Render Market tab content
  const renderMarketTab = () => {
    return (
      <div ref={mainContentRef} style={mainContentStyle}>
        {/* LEFT PANEL - Item Browser */}
        <div style={{ ...leftPanelStyle, width: leftPanelWidth, minWidth: leftPanelWidth }}>
          {/* Search */}
          <div style={searchContainerStyle}>
            <FiSearch size={14} style={{ color: PALETTE.DIM }} />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={searchInputStyle}
            />
            {searchQuery && (
              <button style={clearSearchStyle} onClick={() => setSearchQuery('')}>
                <FiX size={12} />
              </button>
            )}
          </div>

          {/* Filters */}
          <div style={filterRowStyle}>
            <label style={checkboxLabelStyle} onClick={() => setFilterAvailable(!filterAvailable)}>
              <span style={{
                ...checkboxStyle,
                backgroundColor: filterAvailable ? PALETTE.TEAL : 'transparent',
                borderColor: filterAvailable ? PALETTE.TEAL : PALETTE.DIM,
              }}>
                {filterAvailable && <FiCheck size={10} />}
              </span>
              <span>Available</span>
            </label>
            <label style={checkboxLabelStyle} onClick={() => setFilterOwned(!filterOwned)}>
              <span style={{
                ...checkboxStyle,
                backgroundColor: filterOwned ? PALETTE.TEAL : 'transparent',
                borderColor: filterOwned ? PALETTE.TEAL : PALETTE.DIM,
              }}>
                {filterOwned && <FiCheck size={10} />}
              </span>
              <span>Owned</span>
            </label>
          </div>

          {/* Category Filter */}
          <div style={categoryFilterContainerStyle}>
            {CATEGORY_FILTERS.map(cf => (
              <button
                key={cf.value}
                style={{
                  ...categoryPillStyle,
                  backgroundColor: categoryFilter === cf.value ? PALETTE.TEAL : 'transparent',
                  color: categoryFilter === cf.value ? PALETTE.DARK : PALETTE.DIM,
                  borderColor: categoryFilter === cf.value ? PALETTE.TEAL : PALETTE.BORDER,
                }}
                onClick={() => {
                  if (categoryFilter === cf.value) {
                    setCategoryFilter('all');
                  } else {
                    setCategoryFilter(cf.value);
                  }
                  setSubCategoryFilter('all');
                }}
              >
                {cf.label}
              </button>
            ))}
          </div>

          {/* Sub-Category Filter (collapsible) */}
          {SUB_CATEGORY_FILTERS[categoryFilter] && (
            <div style={subCategoryFilterContainerStyle}>
              {SUB_CATEGORY_FILTERS[categoryFilter].map(sf => (
                <button
                  key={sf.value}
                  style={{
                    ...subCategoryPillStyle,
                    backgroundColor: subCategoryFilter === sf.value ? PALETTE.ORANGE : 'transparent',
                    color: subCategoryFilter === sf.value ? PALETTE.DARK : PALETTE.DIM,
                    borderColor: subCategoryFilter === sf.value ? PALETTE.ORANGE : PALETTE.BORDER,
                  }}
                  onClick={() => setSubCategoryFilter(subCategoryFilter === sf.value ? 'all' : sf.value)}
                >
                  {sf.label}
                </button>
              ))}
            </div>
          )}

          {/* Item List */}
          <div style={itemListStyle}>
            {filteredItems.length === 0 ? (
              <div style={emptyStyle}>No items found</div>
            ) : (
              filteredItems.map(item => (
                <div
                  key={item.itemId}
                  style={{
                    ...itemRowStyle,
                    backgroundColor: selectedItem?.itemId === item.itemId ? PALETTE.BORDER : 'transparent',
                  }}
                  onClick={() => handleSelectItem(item)}
                >
                  <span style={itemNameStyle}>{item.itemName}</span>
                  {item.owned > 0 && (
                    <span style={ownedBadgeStyle} title={`Ship: ${item.ownedCargo} | Bank: ${item.ownedBank}`}>
                      x{item.owned}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Chat Section */}
          <div style={chatSectionStyle}>
            {/* Chat Header / Collapsed Bar */}
            <div
              style={chatHeaderStyle}
              onClick={() => setIsChatExpanded(!isChatExpanded)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiMessageSquare size={14} style={{ color: PALETTE.TEAL }} />
                <span style={{ fontSize: '11px', color: PALETTE.LIGHT }}>CHAT</span>
              </div>
              {isChatExpanded ? (
                <FiChevronDown size={14} style={{ color: PALETTE.DIM }} />
              ) : (
                <FiChevronUp size={14} style={{ color: PALETTE.DIM }} />
              )}
            </div>

            {/* Expanded Chat */}
            {isChatExpanded && (
              <div style={chatExpandedStyle}>
                {/* Channel Tabs */}
                <div style={chatTabsStyle}>
                  {(['global', 'local', 'party'] as ChatChannel[]).map(channel => (
                    <div
                      key={channel}
                      style={{
                        ...chatTabStyle,
                        color: channel === chatChannel ? PALETTE.TEAL : PALETTE.DIM,
                        borderBottomColor: channel === chatChannel ? PALETTE.TEAL : 'transparent',
                      }}
                      onClick={() => setChatChannel(channel)}
                    >
                      {channel.toUpperCase()}
                    </div>
                  ))}
                </div>

                {/* Messages */}
                <div style={chatMessagesStyle}>
                  {filteredChatMessages.length === 0 ? (
                    <div style={{ color: PALETTE.DIM, textAlign: 'center', fontSize: '11px', marginTop: '20px' }}>
                      No messages yet
                    </div>
                  ) : (
                    filteredChatMessages.map(msg => (
                      <div key={msg.id} style={chatMessageStyle}>
                        <span style={{ color: PALETTE.DIM, marginRight: '4px', fontSize: '10px' }}>
                          [{formatChatTime(msg.timestamp)}]
                        </span>
                        {msg.isSystem ? (
                          <span style={{ color: PALETTE.DIM, fontStyle: 'italic' }}>{msg.message}</span>
                        ) : (
                          <>
                            <span style={{ color: PALETTE.TEAL, fontWeight: 600 }}>{msg.playerName}:</span>{' '}
                            <span style={{ color: PALETTE.LIGHT }}>{msg.message}</span>
                          </>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={chatMessagesEndRef} />
                </div>

                {/* Input */}
                <div style={chatInputContainerStyle}>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleChatKeyDown}
                    placeholder={`Message #${chatChannel}...`}
                    maxLength={200}
                    style={chatInputStyle}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Horizontal Divider between Left and Center */}
        <div
          style={horizontalDividerStyle}
          onMouseDown={handleHorizontalDividerMouseDown}
        >
          <div style={horizontalDividerHandleStyle} />
        </div>

        {/* CENTER PANEL - Item Details + Order Book */}
        <div ref={centerPanelRef} style={centerPanelStyle}>
          {/* Item Info Header */}
          <div style={itemHeaderStyle}>
            {selectedItem ? (() => {
              const details = getItemDetails(selectedItem.itemType, selectedItem.itemId);
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={itemTitleStyle}>{selectedItem.itemName}</div>
                      <div style={itemMetaStyle}>
                        <span>{selectedItem.itemType.toUpperCase()}</span>
                        {details?.tier && <span style={{ color: PALETTE.TEAL }}>T{details.tier}</span>}
                        {currentItemOwned > 0 && <span style={{ color: PALETTE.TEAL }}>You own: {currentItemOwned}</span>}
                      </div>
                    </div>
                    {details?.tags && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {details.tags.map(tag => (
                          <span key={tag} style={detailTagStyleFn(tag)}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {details?.description && (
                    <div style={detailDescStyle}>{details.description}</div>
                  )}
                  {details?.stats && details.stats.length > 0 && (
                    <div style={detailStatsGridStyle}>
                      {details.stats.map(s => (
                        <div key={s.label} style={detailStatCellStyle}>
                          <span style={{ color: PALETTE.DIM, fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</span>
                          <span style={{ color: PALETTE.LIGHT, fontSize: '11px', fontWeight: 600 }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })() : (
              <div style={emptyStyle}>Select an item to view details</div>
            )}
          </div>

          {/* Center Panel Tabs: Market Data / Price History */}
          {selectedItem && (
            <div style={centerTabsContainerStyle}>
              <div style={centerTabsStyle}>
                <button
                  style={{
                    ...centerTabButtonStyle,
                    color: centerTab === 'market-data' ? PALETTE.TEAL : PALETTE.DIM,
                    borderBottomColor: centerTab === 'market-data' ? PALETTE.TEAL : 'transparent',
                  }}
                  onClick={() => setCenterTab('market-data')}
                >
                  Market Data
                </button>
                <button
                  style={{
                    ...centerTabButtonStyle,
                    color: centerTab === 'price-history' ? PALETTE.TEAL : PALETTE.DIM,
                    borderBottomColor: centerTab === 'price-history' ? PALETTE.TEAL : 'transparent',
                  }}
                  onClick={() => setCenterTab('price-history')}
                >
                  Price History
                </button>
              </div>
            </div>
          )}

          {/* Price History Chart (shown when price-history tab selected) */}
          {selectedItem && centerTab === 'price-history' && (
            <div style={priceHistoryPanelStyle}>
              <PriceHistoryChart
                data={priceHistory}
                itemName={selectedItem.itemName}
                height={280}
              />
            </div>
          )}

          {/* Resizable Sections Container (shown when market-data tab selected) */}
          {(!selectedItem || centerTab === 'market-data') && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Sellers (Asks) */}
            <div style={{ ...orderSectionStyle, height: `${sellersHeight}%`, minHeight: '80px' }}>
              <div style={orderSectionTitleStyle}>
                <span style={{ color: PALETTE.RED }}>SELLERS</span>
              </div>
              <div style={orderColumnHeaderRowStyle3Col}>
                <span>Price</span>
                <span>Qty</span>
                <span>Expires In</span>
              </div>
              <div style={orderListStyle}>
                {asks.length === 0 ? (
                  mmPrices?.supported && mmPrices?.sellPrice ? (
                    <div style={mmFallbackStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: PALETTE.ORANGE }}>Market Maker</span>
                        <span style={{ color: PALETTE.ORANGE }}>{formatNumber(mmPrices.sellPrice)} CR</span>
                      </div>
                      <span style={{ color: PALETTE.DIM, fontSize: '10px' }}>
                        {mmPrices.sellLimitRemaining || 0} / {getMmDailyLimit(mmPrices.activityLevel)} units available today
                      </span>
                    </div>
                  ) : (
                    <div style={noOrdersStyle}>No sell orders</div>
                  )
                ) : (
                  [...asks].slice(0, 10).map((entry, i) => (
                    <div key={`ask-${i}`} style={orderRowDataStyle3Col}>
                      <span style={{ color: PALETTE.RED }}>{formatNumber(entry.price)} CR</span>
                      <span>{formatNumber(entry.quantity)}</span>
                      <span style={{ color: PALETTE.DIM }}>{formatExpiresIn(entry.earliestExpiry)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Divider 1: Between Sellers and Buyers */}
            <div
              style={dividerStyle}
              onMouseDown={(e) => handleDividerMouseDown(e, 'sellers-buyers')}
            >
              <div style={dividerHandleStyle} />
            </div>

            {/* Spread */}
            {selectedItem && (
              <div style={spreadStyle}>
                {asks.length > 0 && bids.length > 0 ? (
                  `Spread: ${formatNumber(asks[0].price - bids[0].price)} CR`
                ) : (
                  'No spread data'
                )}
              </div>
            )}

            {/* Buyers (Bids) */}
            <div style={{ ...orderSectionStyle, height: `${buyersHeight}%`, minHeight: '80px' }}>
              <div style={orderSectionTitleStyle}>
                <span style={{ color: PALETTE.GREEN }}>BUYERS</span>
              </div>
              <div style={orderColumnHeaderRowStyle3Col}>
                <span>Price</span>
                <span>Qty</span>
                <span>Expires In</span>
              </div>
              <div style={orderListStyle}>
                {bids.length === 0 ? (
                  mmPrices?.supported && mmPrices?.buyPrice ? (
                    <div style={mmFallbackStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: PALETTE.ORANGE }}>Market Maker</span>
                        <span style={{ color: PALETTE.ORANGE }}>{formatNumber(mmPrices.buyPrice)} CR</span>
                      </div>
                      <span style={{ color: PALETTE.DIM, fontSize: '10px' }}>
                        {mmPrices.buyLimitRemaining || 0} / {getMmDailyLimit(mmPrices.activityLevel)} units available today
                      </span>
                    </div>
                  ) : (
                    <div style={noOrdersStyle}>No buy orders</div>
                  )
                ) : (
                  bids.slice(0, 10).map((entry, i) => (
                    <div key={`bid-${i}`} style={orderRowDataStyle3Col}>
                      <span style={{ color: PALETTE.GREEN }}>{formatNumber(entry.price)} CR</span>
                      <span>{formatNumber(entry.quantity)}</span>
                      <span style={{ color: PALETTE.DIM }}>{formatExpiresIn(entry.earliestExpiry)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Divider 2: Between Buyers and Recent Trades */}
            <div
              style={dividerStyle}
              onMouseDown={(e) => handleDividerMouseDown(e, 'buyers-trades')}
            >
              <div style={dividerHandleStyle} />
            </div>

            {/* Recent Trades */}
            <div style={{ ...recentTradesContainerStyle, height: `${tradesHeight}%`, minHeight: '100px' }}>
              <div style={orderSectionTitleStyle}>
                <span style={{ color: PALETTE.TEAL }}>RECENT TRADES</span>
              </div>
              <div style={tradesColumnHeaderRowStyle}>
                <span>Price</span>
                <span>Qty</span>
                <span>Time</span>
              </div>
              <div style={recentTradesListStyle}>
                {recentTrades.length === 0 ? (
                  <div style={noOrdersStyle}>No recent trades</div>
                ) : (
                  recentTrades.slice(0, 15).map((trade) => (
                    <div key={trade.id} style={tradeRowStyle}>
                      <span>{formatNumber(trade.pricePerUnit)} CR</span>
                      <span>x{formatNumber(trade.quantity)}</span>
                      <span style={{ color: PALETTE.DIM }}>{formatTradeTime(trade.executedAt)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* RIGHT PANEL - Order Entry + My Orders */}
        <div style={rightPanelStyle}>
          {/* Order Type Toggle */}
          <div style={orderTypeToggleStyle}>
            <button
              style={{
                ...orderTypeButtonStyle,
                backgroundColor: orderType === 'buy' ? PALETTE.GREEN : 'transparent',
                color: orderType === 'buy' ? PALETTE.DARK : PALETTE.GREEN,
                borderColor: PALETTE.GREEN,
              }}
              onClick={() => setOrderType('buy')}
            >
              BUY
            </button>
            <button
              style={{
                ...orderTypeButtonStyle,
                backgroundColor: orderType === 'sell' ? PALETTE.RED : 'transparent',
                color: orderType === 'sell' ? PALETTE.DARK : PALETTE.RED,
                borderColor: PALETTE.RED,
                opacity: currentItemOwned === 0 ? 0.4 : 1,
                cursor: currentItemOwned === 0 ? 'not-allowed' : 'pointer',
              }}
              onClick={() => currentItemOwned > 0 && setOrderType('sell')}
              title={currentItemOwned === 0 ? 'You don\'t own this item' : undefined}
            >
              SELL
            </button>
          </div>

          {/* Destination (buy) / Source (sell) Toggle - hidden for ships */}
          {selectedItem?.itemType !== 'ship' && (
          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
            <span style={{ fontSize: '10px', color: PALETTE.DIM, alignSelf: 'center', marginRight: '4px' }}>
              {orderType === 'buy' ? 'To:' : 'From:'}
            </span>
            {(() => {
              const isCargoFull = cargoUsed >= cargoCapacity;
              const cargoSelected = (orderType === 'buy' ? buyDestination : sellSource) === 'cargo';
              const cargoDisabled = orderType === 'buy' && isCargoFull;
              return (
                <>
                  <button
                    style={{
                      ...modeButtonStyle,
                      flex: 1,
                      backgroundColor: cargoSelected && !cargoDisabled ? PALETTE.PANEL : 'transparent',
                      borderColor: cargoSelected && !cargoDisabled ? PALETTE.TEAL : PALETTE.BORDER,
                      opacity: cargoDisabled ? 0.4 : 1,
                      cursor: cargoDisabled ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => {
                      if (cargoDisabled) return;
                      orderType === 'buy' ? setBuyDestination('cargo') : setSellSource('cargo');
                    }}
                    title={cargoDisabled ? `Cargo full (${cargoUsed}/${cargoCapacity})` : `Ship cargo (${cargoUsed}/${cargoCapacity})`}
                  >
                    Ship {orderType === 'sell' && currentItemOwnedCargo > 0 ? `(${currentItemOwnedCargo})` : ''}
                  </button>
                  <button
                    style={{
                      ...modeButtonStyle,
                      flex: 1,
                      backgroundColor: (orderType === 'buy' ? buyDestination : sellSource) === 'bank' ? PALETTE.PANEL : 'transparent',
                      borderColor: (orderType === 'buy' ? buyDestination : sellSource) === 'bank' ? PALETTE.TEAL : PALETTE.BORDER,
                    }}
                    onClick={() => orderType === 'buy' ? setBuyDestination('bank') : setSellSource('bank')}
                  >
                    Bank {orderType === 'sell' && currentItemOwnedBank > 0 ? `(${currentItemOwnedBank})` : ''}
                  </button>
                </>
              );
            })()}
          </div>
          )}

          {/* Order Mode Toggle */}
          <div style={modeToggleStyle}>
            <button
              style={{
                ...modeButtonStyle,
                backgroundColor: orderMode === 'limit' ? PALETTE.PANEL : 'transparent',
                borderColor: orderMode === 'limit' ? PALETTE.TEAL : PALETTE.BORDER,
              }}
              onClick={() => setOrderMode('limit')}
            >
              Limit
            </button>
            <button
              style={{
                ...modeButtonStyle,
                backgroundColor: orderMode === 'market' ? PALETTE.PANEL : 'transparent',
                borderColor: orderMode === 'market' ? PALETTE.TEAL : PALETTE.BORDER,
              }}
              onClick={() => setOrderMode('market')}
            >
              Market
            </button>
          </div>

          {/* Price Input */}
          <div style={{
            ...inputGroupStyle,
            opacity: orderMode === 'market' ? 0.5 : 1,
          }}>
            <label style={inputLabelStyle}>Price</label>
            <div style={inputRowStyle}>
              <button
                style={inputBtnStyle}
                onClick={() => setOrderPrice(p => String(Math.max(1, (parseInt(p) || 0) - 1)))}
                disabled={orderMode === 'market'}
              >
                <FiMinus size={12} />
              </button>
              <input
                type="number"
                value={orderMode === 'market' ? '' : orderPrice}
                onChange={(e) => setOrderPrice(e.target.value)}
                disabled={orderMode === 'market'}
                placeholder={orderMode === 'market' ? 'Market Price' : '0'}
                style={inputFieldStyle}
              />
              <button
                style={inputBtnStyle}
                onClick={() => setOrderPrice(p => String((parseInt(p) || 0) + 1))}
                disabled={orderMode === 'market'}
              >
                <FiPlus size={12} />
              </button>
            </div>
          </div>

          {/* Quantity Input */}
          <div style={inputGroupStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={inputLabelStyle}>Quantity</label>
              {orderType === 'sell' && currentSellableQuantity > 0 && (
                <button
                  style={maxButtonStyle}
                  onClick={() => setOrderQuantity(String(currentSellableQuantity))}
                >
                  Max ({currentSellableQuantity})
                </button>
              )}
            </div>
            <div style={inputRowStyle}>
              <button style={inputBtnStyle} onClick={() => setOrderQuantity(q => String(Math.max(1, (parseInt(q) || 0) - 1)))}>
                <FiMinus size={12} />
              </button>
              <input
                type="number"
                value={orderQuantity}
                onChange={(e) => {
                  const newVal = e.target.value;
                  // When selling, cap at owned amount from selected source
                  if (orderType === 'sell' && currentSellableQuantity > 0) {
                    const num = parseInt(newVal, 10);
                    if (!isNaN(num) && num > currentSellableQuantity) {
                      setOrderQuantity(String(currentSellableQuantity));
                      return;
                    }
                  }
                  setOrderQuantity(newVal);
                }}
                min="1"
                max={orderType === 'sell' ? currentSellableQuantity : undefined}
                style={inputFieldStyle}
              />
              <button
                style={inputBtnStyle}
                onClick={() => {
                  setOrderQuantity(q => {
                    const newQty = (parseInt(q) || 0) + 1;
                    // When selling, cap at owned amount from selected source
                    if (orderType === 'sell' && currentSellableQuantity > 0 && newQty > currentSellableQuantity) {
                      return String(currentSellableQuantity);
                    }
                    return String(newQty);
                  });
                }}
              >
                <FiPlus size={12} />
              </button>
            </div>
          </div>

          {/* Totals */}
          <div style={totalsStyle}>
            <div style={totalRowStyle}>
              <span>Subtotal</span>
              <span>{formatNumber(subtotal)} CR</span>
            </div>
            <div style={{ ...totalRowStyle, fontSize: '11px', color: PALETTE.DIM }}>
              <span>Fee ({(MARKETPLACE_CONFIG.TRANSACTION_FEE_RATE * 100).toFixed(0)}%)</span>
              <span>{formatNumber(fee)} CR</span>
            </div>
            {launderUnits > 0 && !isSettlementMarket && (
              <div style={{ ...totalRowStyle, fontSize: '11px', color: '#ff6655' }}
                   title="Stolen goods: extra laundering fee at NPC stations. Sell at a rogue settlement to avoid it.">
                <span>⚠ Laundering ({(launderingRate * 100).toFixed(0)}% × {launderUnits} stolen)</span>
                <span>-{formatNumber(wouldBeLaundering)} CR</span>
              </div>
            )}
            {launderUnits > 0 && isSettlementMarket && (
              <div style={{ ...totalRowStyle, fontSize: '11px', color: '#55cc77' }}
                   title="Settlements don't charge the laundering fee on stolen goods.">
                <span>✓ Laundering waived (settlement)</span>
                <span>+{formatNumber(wouldBeLaundering)} CR saved</span>
              </div>
            )}
            <div style={{ ...totalRowStyle, fontWeight: 600, borderTop: `1px solid ${PALETTE.BORDER}`, paddingTop: '8px', marginTop: '8px' }}>
              <span>Total</span>
              <span>{formatNumber(total)} CR</span>
            </div>
          </div>

          {/* Place Order Button */}
          <button
            style={{
              ...placeOrderButtonStyle,
              backgroundColor: selectedItem && !isPlacingOrder ? (orderType === 'buy' ? PALETTE.GREEN : PALETTE.RED) : PALETTE.DIM,
              cursor: selectedItem && !isPlacingOrder ? 'pointer' : 'not-allowed',
            }}
            onClick={handlePlaceOrder}
            disabled={!selectedItem || isPlacingOrder}
          >
            {isPlacingOrder ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiLoader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                PLACING...
              </span>
            ) : (
              `PLACE ${orderType.toUpperCase()} ORDER`
            )}
          </button>
        </div>
      </div>
    );
  };

  // Render My Orders tab content
  const renderMyOrdersTab = () => {
    return (
      <div style={myOrdersTabStyle}>
        <div style={myOrdersHeaderStyle}>
          <span>Type</span>
          <span>Item</span>
          <span>Price</span>
          <span>Filled</span>
          <span>Action</span>
        </div>
        {playerOrders.length === 0 ? (
          <div style={myOrdersEmptyStyle}>You have no active orders</div>
        ) : (
          <div style={myOrdersListFullStyle}>
            {playerOrders.map((order: any) => {
              const ot = order.orderType || order.order_type || 'buy';
              const itemId = order.itemId || order.item_id || '';
              const pricePerUnit = order.pricePerUnit || order.price_per_unit || 0;
              const fulfilledQuantity = order.fulfilledQuantity || order.fulfilled_quantity || 0;
              const qty = order.quantity || 0;

              return (
                <div key={order.id} style={myOrdersRowStyle}>
                  <span style={{ color: ot === 'buy' ? PALETTE.GREEN : PALETTE.RED, fontWeight: 600 }}>
                    {ot.toUpperCase()}
                  </span>
                  <span>{getItemDisplayName(itemId)}</span>
                  <span>{formatNumber(pricePerUnit)} CR</span>
                  <span>{fulfilledQuantity}/{qty}</span>
                  <button
                    style={cancelBtnStyle}
                    onClick={() => handleCancelOrder(order.id)}
                    disabled={cancellingOrderId !== null}
                  >
                    {cancellingOrderId === order.id ? (
                      <FiLoader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <FiX size={12} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>

      {/* Header */}
      <div style={headerStyle}>
        <button style={backButtonStyle} onClick={onBack}>
          <FiArrowLeft size={16} />
          <span>BACK</span>
        </button>
        <div style={headerTabsStyle}>
          <button
            style={{
              ...headerTabStyle,
              color: activeTab === 'market' ? PALETTE.TEAL : PALETTE.DIM,
              borderBottomColor: activeTab === 'market' ? PALETTE.TEAL : 'transparent',
            }}
            onClick={() => setActiveTab('market')}
          >
            Market
          </button>
          <button
            style={{
              ...headerTabStyle,
              color: activeTab === 'my-orders' ? PALETTE.TEAL : PALETTE.DIM,
              borderBottomColor: activeTab === 'my-orders' ? PALETTE.TEAL : 'transparent',
            }}
            onClick={() => setActiveTab('my-orders')}
          >
            My Orders
          </button>
        </div>
        <span style={hintStyle}>[ESC] Return to Station</span>
      </div>

      {/* Tab Content */}
      {activeTab === 'market' ? renderMarketTab() : renderMyOrdersTab()}
    </div>
  );
}

// ==================== STYLES ====================

const containerStyle = stationContainerStyle;
const headerStyle = stationHeaderStyle;
const backButtonStyle = stationBackButtonStyle;
const hintStyle = stationHintStyle;
const headerTabsStyle = stationHeaderTabsStyle;
// MarketShop applies active/inactive colors inline, so use the base style from the function
const headerTabStyle: React.CSSProperties = stationHeaderTabStyleFn(false);

const mainContentStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

// Left Panel
const leftPanelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const searchContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: 'transparent',
  border: 'none',
  color: PALETTE.LIGHT,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  outline: 'none',
};

const clearSearchStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 'none',
  color: PALETTE.DIM,
  cursor: 'pointer',
  padding: '2px',
  display: 'flex',
};

const filterRowStyle: React.CSSProperties = {
  display: 'flex',
  padding: '8px 12px',
  gap: '12px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  fontSize: '11px',
  color: PALETTE.LIGHT,
  userSelect: 'none',
};

const checkboxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '14px',
  height: '14px',
  border: '1px solid',
  borderRadius: '2px',
  color: PALETTE.DARK,
  transition: 'all 0.1s',
};

const categoryFilterContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  padding: '6px 8px',
  gap: '4px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const categoryPillStyle: React.CSSProperties = {
  padding: '2px 8px',
  fontSize: '10px',
  fontFamily: "'JetBrains Mono', monospace",
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  border: '1px solid',
  borderRadius: '3px',
  cursor: 'pointer',
  transition: 'all 0.15s',
  background: 'none',
};

const subCategoryFilterContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  padding: '4px 8px 6px',
  gap: '4px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  backgroundColor: PALETTE.PANEL,
};

const subCategoryPillStyle: React.CSSProperties = {
  padding: '1px 6px',
  fontSize: '9px',
  fontFamily: "'JetBrains Mono', monospace",
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  border: '1px solid',
  borderRadius: '2px',
  cursor: 'pointer',
  transition: 'all 0.15s',
  background: 'none',
};

const itemListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
};

const itemRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  cursor: 'pointer',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  transition: 'background-color 0.1s',
};

const itemNameStyle: React.CSSProperties = {
  fontSize: '11px',
};

const ownedBadgeStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.TEAL,
  backgroundColor: PALETTE.PANEL,
  padding: '2px 6px',
  borderRadius: '3px',
};

const emptyStyle: React.CSSProperties = {
  padding: '20px',
  textAlign: 'center',
  color: PALETTE.DIM,
  fontSize: '11px',
};

// Center Panel
const centerPanelStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${PALETTE.BORDER}`,
  overflow: 'hidden',
};

const itemHeaderStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  minHeight: '70px',
};

const detailDescStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  marginTop: '6px',
  lineHeight: '1.4',
};

function getTagColor(tag: string): string {
  const t = tag.toLowerCase();
  // Ship classes
  if (['frigate', 'destroyer', 'cruiser', 'battleship', 'carrier', 'dreadnought', 'corvette'].includes(t)) return PALETTE.TEAL;
  // Roles
  if (['combat', 'mining', 'trading', 'exploration', 'support', 'hybrid'].includes(t)) return PALETTE.ORANGE;
  // Sizes
  if (['small', 'medium', 'large'].includes(t)) return PALETTE.YELLOW;
  // Equipment categories
  if (['weapon', 'shield', 'engine', 'tool', 'utility', 'drone'].includes(t)) return SLOT_COLORS[t.toUpperCase()] || PALETTE.DIM;
  return PALETTE.DIM;
}

const detailTagStyleFn = (tag: string): React.CSSProperties => {
  const color = getTagColor(tag);
  return {
    padding: '1px 6px',
    fontSize: '9px',
    fontFamily: "'JetBrains Mono', monospace",
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    border: `1px solid ${color}`,
    borderRadius: '2px',
    color: color,
    backgroundColor: `${color}15`,
  };
};

const detailStatsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
  gap: '4px',
  marginTop: '8px',
};

const detailStatCellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  padding: '4px 6px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '3px',
};

const itemTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  marginBottom: '4px',
};

const itemMetaStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  fontSize: '11px',
  color: PALETTE.DIM,
};

// Center Panel Tabs
const centerTabsContainerStyle: React.CSSProperties = {
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  backgroundColor: PALETTE.PANEL,
};

const centerTabsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0',
  padding: '0 16px',
};

const centerTabButtonStyle: React.CSSProperties = {
  padding: '10px 16px',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.03em',
  transition: 'color 0.15s',
};

const priceHistoryPanelStyle: React.CSSProperties = {
  padding: '16px',
  flex: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const orderSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const orderSectionTitleStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: PALETTE.PANEL,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.05em',
};

const orderColumnHeaderRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  padding: '4px 16px',
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const orderColumnHeaderRowStyle3Col: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 0.8fr 1.2fr',
  padding: '4px 16px',
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const orderListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
};

const orderRowDataStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  padding: '4px 16px',
  fontSize: '11px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const orderRowDataStyle3Col: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 0.8fr 1.2fr',
  padding: '4px 16px',
  fontSize: '11px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const noOrdersStyle: React.CSSProperties = {
  padding: '12px 16px',
  color: PALETTE.DIM,
  fontSize: '11px',
};

// Market Maker fallback display style
const mmFallbackStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'rgba(221, 86, 57, 0.1)', // PALETTE.ORANGE with transparency
  borderLeft: `3px solid ${PALETTE.ORANGE}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '11px',
};

const spreadStyle: React.CSSProperties = {
  padding: '6px 16px',
  textAlign: 'center',
  fontSize: '11px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.PANEL,
  flexShrink: 0,
};

const dividerStyle: React.CSSProperties = {
  height: '8px',
  backgroundColor: PALETTE.BORDER,
  cursor: 'row-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const dividerHandleStyle: React.CSSProperties = {
  width: '40px',
  height: '3px',
  backgroundColor: PALETTE.DIM,
  borderRadius: '2px',
};

const horizontalDividerStyle: React.CSSProperties = {
  width: '8px',
  backgroundColor: PALETTE.BORDER,
  cursor: 'col-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const horizontalDividerHandleStyle: React.CSSProperties = {
  width: '3px',
  height: '40px',
  backgroundColor: PALETTE.DIM,
  borderRadius: '2px',
};

const recentTradesContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const tradesColumnHeaderRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  padding: '4px 16px',
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const recentTradesListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
};

const tradeRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  padding: '4px 16px',
  fontSize: '11px',
  color: PALETTE.LIGHT,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

// Right Panel
const rightPanelStyle: React.CSSProperties = {
  width: '280px',
  minWidth: '280px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  overflowY: 'auto',
};

const orderTypeToggleStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const orderTypeButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '5px 8px',
  border: '1px solid',
  borderRadius: '3px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  fontWeight: 600,
  transition: 'all 0.15s',
};

const modeToggleStyle: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
};

const modeButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '4px 6px',
  border: '1px solid',
  borderRadius: '3px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '10px',
  color: PALETTE.LIGHT,
  backgroundColor: 'transparent',
};

const inputGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const inputLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
};

const maxButtonStyle: React.CSSProperties = {
  padding: '2px 8px',
  backgroundColor: 'transparent',
  border: `1px solid ${PALETTE.TEAL}`,
  borderRadius: '3px',
  color: PALETTE.TEAL,
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '10px',
};

const inputRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
};

const inputBtnStyle: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: '3px',
  color: PALETTE.LIGHT,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
};

const inputFieldStyle: React.CSSProperties = {
  flex: 1,
  padding: '5px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: '3px',
  color: PALETTE.LIGHT,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  textAlign: 'center',
};

const totalsStyle: React.CSSProperties = {
  padding: '8px 10px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '3px',
};

const totalRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '11px',
  marginBottom: '4px',
};

const placeOrderButtonStyle: React.CSSProperties = {
  padding: '8px',
  border: 'none',
  borderRadius: '3px',
  color: PALETTE.DARK,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const myOrdersStyle: React.CSSProperties = {
  marginTop: 'auto',
  borderTop: `1px solid ${PALETTE.BORDER}`,
  paddingTop: '12px',
};

const myOrdersListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const myOrderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '4px',
  gap: '8px',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '6px',
  backgroundColor: 'transparent',
  border: `1px solid ${PALETTE.RED}`,
  borderRadius: '3px',
  color: PALETTE.RED,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
};

// Chat styles
const chatSectionStyle: React.CSSProperties = {
  borderTop: `1px solid ${PALETTE.BORDER}`,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  marginTop: 'auto',
};

const chatHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  cursor: 'pointer',
  backgroundColor: PALETTE.PANEL,
  transition: 'background-color 0.15s',
  flexShrink: 0,
};

const chatExpandedStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: PALETTE.DARK,
  height: '300px',
  overflow: 'hidden',
};

const chatTabsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 0,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  flexShrink: 0,
};

const chatTabStyle: React.CSSProperties = {
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: '10px',
  borderBottom: '2px solid transparent',
  transition: 'color 0.2s',
};

const chatMessagesStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minHeight: 0,
};

const chatMessageStyle: React.CSSProperties = {
  wordBreak: 'break-word',
  lineHeight: 1.3,
  fontSize: '11px',
};

const chatInputContainerStyle: React.CSSProperties = {
  padding: '8px',
  borderTop: `1px solid ${PALETTE.BORDER}`,
  flexShrink: 0,
  backgroundColor: PALETTE.DARK,
};

const chatInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: '4px',
  color: PALETTE.LIGHT,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '11px',
  outline: 'none',
  boxSizing: 'border-box',
};

// My Orders Tab styles
const myOrdersTabStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: '16px',
};

const myOrdersHeaderStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '80px 1fr 120px 100px 60px',
  padding: '12px 16px',
  backgroundColor: PALETTE.PANEL,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.DIM,
  letterSpacing: '0.05em',
};

const myOrdersEmptyStyle: React.CSSProperties = {
  padding: '40px',
  textAlign: 'center',
  color: PALETTE.DIM,
  fontSize: '11px',
};

const myOrdersListFullStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
};

const myOrdersRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '80px 1fr 120px 100px 60px',
  padding: '12px 16px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  fontSize: '11px',
  alignItems: 'center',
};

export default MarketShop;
